// =============================================================================
//  apiPageBuilder.js — Fonctions d'appel API pour le PageBuilder
//  Base URL : elakay_API + /pageBuilder
//  Auth      : Bearer token (header Authorization)
// =============================================================================

import { elakay_API } from "../../config";
import { isAuthenticated } from "../auth";

// Token courant (rechargé à chaque import du module)
const getToken = () => isAuthenticated()?.token || "";

// Helper fetch commun
async function apiFetch(path, { method = "GET", body, token } = {}) {
  const headers = {
    Accept: "application/json",
    "Content-Type": "application/json",
  };
  if (token) headers.Authorization = `Bearer ${token}`;

  const opts = { method, headers };
  if (body !== undefined) opts.body = JSON.stringify(body);

  const res = await fetch(`${elakay_API}${path}`, opts);
  const data = await res.json();
  if (!res.ok) {
    // `status`/`code` let callers distinguish e.g. a 404 "this page no
    // longer exists" from a 403 permission error reliably, instead of
    // matching the (French, human-facing) `.message` text -- see
    // index.jsx's handleSave/performQuickSave/handleEditUpdate, which use
    // `error.status === 404` to recover a page deleted server-side by
    // re-saving it as a brand new one instead of losing the user's work.
    const error = new Error(data.error || `Erreur ${res.status}`);
    error.status = res.status;
    error.code = data.code;
    throw error;
  }
  return data;
}

// =============================================================================
//  CREATE — POST /pageBuilder
//  Crée un nouveau document PageBuilder dans la base de données.
//
//  @param {object} payload  – { doc, title, description, pageMode, isTemplate,
//                               forSale, price, tags, thumbnail, status }
//  @param {string} [token]  – JWT (optionnel, fallback sur isAuthenticated)
// =============================================================================
export const createPageBuilder = (payload, token) =>
  apiFetch("/pageBuilder", { method: "POST", body: payload, token: token || getToken() });

// =============================================================================
//  UPDATE — PATCH /pageBuilder/:id
//  Met à jour un document existant (owner uniquement).
// =============================================================================
export const updatePageBuilder = (id, payload, token) =>
  apiFetch(`/pageBuilder/${id}`, { method: "PATCH", body: payload, token: token || getToken() });

const normalizePageBuilderPayload = (payload) => {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    return payload;
  }

  if (payload.doc !== undefined) return payload;

  if (payload.pageBuilder && typeof payload.pageBuilder === "object") {
    const { pageBuilder, ...metadata } = payload;
    return { ...metadata, doc: pageBuilder };
  }

  // Autorise savePageBuilder(docJson, id, token) directement.
  if (Array.isArray(payload.pages) || payload.page?.children) {
    return {
      doc: payload,
      title: payload.meta?.title || payload.pages?.[0]?.name || "Ma page",
      description: payload.meta?.description || "",
      pageMode: payload.pageMode || "static",
    };
  }

  return payload;
};

// =============================================================================
//  SAVE OR UPDATE (createOrUpdate)
//  Si `id` est fourni → updatePageBuilder, sinon → createPageBuilder.
//  Renvoie le document sauvegardé (avec son _id MongoDB).
// =============================================================================
export const savePageBuilder = (payload, id, token) => {
  const normalizedPayload = normalizePageBuilderPayload(payload);
  return (
    id
      ? updatePageBuilder(id, normalizedPayload, token)
      : createPageBuilder(normalizedPayload, token)
  );
};

// =============================================================================
//  GET MY PAGES — GET /pageBuilder/mine
//  Retourne { pages: [...], remaining: number }
//
//  @param {object} params – { pageMode?, status?, isTemplate?, skip?, limit? }
// =============================================================================
export const getMyPageBuilders = (params = {}, token) => {
  const qs = new URLSearchParams(
    Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== ""))
  ).toString();
  return apiFetch(`/pageBuilder/mine${qs ? `?${qs}` : ""}`, { token: token || getToken() });
};

// =============================================================================
//  GET ONE — GET /pageBuilder/:id
//  Récupère un document complet (owner ou template public).
// =============================================================================
export const getPageBuilderById = (id, token) =>
  apiFetch(`/pageBuilder/${id}`, { token: token || getToken() });

// =============================================================================
//  GET PUBLIC TEMPLATES — GET /pageBuilder/templates  (sans auth)
//  Retourne { templates: [...] }
//
//  @param {object} params – { pageMode?, skip?, limit? }
// =============================================================================
export const getPublicTemplates = (params = {}) => {
  const qs = new URLSearchParams(
    Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== ""))
  ).toString();
  return apiFetch(`/pageBuilder/templates${qs ? `?${qs}` : ""}`);
};

// =============================================================================
//  DELETE — DELETE /pageBuilder/:id
// =============================================================================
export const deletePageBuilder = (id, token) =>
  apiFetch(`/pageBuilder/${id}`, { method: "DELETE", token: token || getToken() });

// =============================================================================
//  CHANGE STATUS — PATCH /pageBuilder/:id/status
//  @param {string} status – "draft" | "published" | "purchased" | "archived"
// =============================================================================
export const changePageBuilderStatus = (id, status, token) =>
  apiFetch(`/pageBuilder/${id}/status`, { method: "PATCH", body: { status }, token: token || getToken() });

// =============================================================================
//  PURCHASE / COPY TEMPLATE — POST /pageBuilder/:id/purchase
//  Crée une copie du template dans le compte de l'utilisateur connecté.
// =============================================================================
export const purchasePageBuilder = (id, token) =>
  apiFetch(`/pageBuilder/${id}/purchase`, { method: "POST", token: token || getToken() });

// =============================================================================
// FAMILY ACCESS
// Vérifie le mot de passe du compte Family connecté, puis permet de créer une
// copie indépendante et modifiable d'une page en lecture seule.
// =============================================================================
export const verifyFamilyPageBuilderAccess = (password, token) =>
  apiFetch("/pageBuilder/family/verify", {
    method: "POST",
    body: { password },
    token: token || getToken(),
  });

export const familyCopyPageBuilder = (id, password, token) =>
  apiFetch(`/pageBuilder/${id}/family-copy`, {
    method: "POST",
    body: { password },
    token: token || getToken(),
  });

// =============================================================================
// EDIT HTML CODE (password-gated)
// Le mot de passe n'est envoyé qu'à cet unique endpoint (jamais avec
// save/restore) -- il revient une autorisation temporaire (confirmationToken,
// quelques minutes) que le frontend joint ensuite aux appels save/restore.
// La permission ET le mot de passe sont de toute façon re-vérifiés côté
// serveur à chaque appel : ceci n'est qu'une commodité pour ne pas redemander
// le mot de passe à chaque frappe.
// =============================================================================
export const verifyPageHtmlEditAccess = (id, password, token) =>
  apiFetch(`/pageBuilder/${id}/verify-html-edit`, {
    method: "POST",
    body: { password },
    token: token || getToken(),
  });

// `pageId` (last param, optional everywhere) is the JSON page's OWN id
// (doc.pages[].id) -- NOT `id` above, which is the PageBuilder document's
// Mongo _id. Omitting it defaults server-side to the document's first page
// (backward compatible with a single-page document/older caller).
export const getPageHtmlSourceAPI = (id, token, pageId) =>
  apiFetch(`/pageBuilder/${id}/html-source${pageId ? `?pageId=${encodeURIComponent(pageId)}` : ""}`, { token: token || getToken() });

export const savePageHtmlSourceAPI = (id, html, confirmationToken, token, pageId, baseEditedAt) =>
  apiFetch(`/pageBuilder/${id}/html-source`, {
    method: "PATCH",
    body: {
      html,
      confirmationToken,
      ...(pageId ? { pageId } : {}),
      ...(baseEditedAt !== undefined ? { baseEditedAt: baseEditedAt || null } : {}),
    },
    token: token || getToken(),
  });

export const restorePageGeneratedHtmlAPI = (id, confirmationToken, token, pageId) =>
  apiFetch(`/pageBuilder/${id}/html-source/restore`, {
    method: "POST",
    body: { confirmationToken, ...(pageId ? { pageId } : {}) },
    token: token || getToken(),
  });
